import { Box, ButtonBS, colors, HStack, IconButtonBS, InputBS, InputGroup, Text } from "@biblioteksentralen/react";
import { FormEventHandler, useEffect, useRef, useState } from "react";
import { X } from "react-feather";
import { useMount } from "react-use";
import { DefaultContainer } from "../../../components/ContentContainer";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { useSitePalette } from "../../../utils/useSitePalette";
import { useIsOnSearchPage } from "../../hooks/useIsOnSearchPage";
import { useSearchConfig } from "../../hooks/useSearchConfig";
import { useSearchState } from "./useSearchState";

export const SearchInput = () => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const paletteColor = useSitePalette().colors.search;
  const searchConfig = useSearchConfig();
  const [inputValue, setInputValue] = useState("");
  const [searchState, setSearchState] = useSearchState();
  const isOnSearchPage = useIsOnSearchPage();

  // Focus the input when navigating to the search page
  useEffect(() => {
    isOnSearchPage && inputRef.current?.focus();
  }, [isOnSearchPage]);

  // Update input value when mounting page with a defined searchQuery (i.e. .../sok?s=<searchQuery>)
  useMount(() => {
    searchState?.s && setInputValue(searchState.s);
  });

  const handleClear: FormEventHandler = () => {
    setInputValue("");
    inputRef.current?.focus();
  };

  const handleSubmit: FormEventHandler = (event) => {
    event.preventDefault();

    // Hvis vi returnerer på !inputValue vil vi hindre nullstilling av søket
    if (inputValue && inputValue === searchState?.s) return;

    setSearchState({
      s: inputValue,
      selectedCategory: undefined,
      work: undefined,
      agent: undefined,
      yourLibrary: undefined,
    });
  };

  const placeholder = t("Hva leter du etter?");

  const ClearButton = inputValue && (
    <IconButtonBS
      onClick={handleClear}
      aria-label={t("Nullstill søk")}
      _hover={{ backgroundColor: "rgba(0,0,0,.05)" }}
      transition="0.3s ease background"
      variant="ghost"
    >
      <X size="1.5rem" />
    </IconButtonBS>
  );

  return (
    <Box
      as="form"
      role="search"
      flexDirection="column"
      onSubmit={handleSubmit}
      width="100%"
      padding={{ base: "1rem 0", lg: "2rem 0" }}
      {...paletteColor.css}
    >
      <DefaultContainer>
        {searchConfig.searchIntegrationEnvironment === "staging" && (
          // eslint-disable-next-line i18next/no-literal-string
          <Text fontSize="xs" marginBottom="1rem">
            Merk: Søket på denne sida går mot staging/test-miljøet til BS Dataplattform. Hvis du ikke får noen treff kan
            det være fordi vi holder på å jobbe med noe der.
          </Text>
        )}
        <HStack
          position="relative"
          width="100%"
          maxWidth="container.lg"
          minWidth="8rem"
          alignItems="flex-end"
          gap="1rem"
        >
          <InputGroup
            // disable webkit styling in the input field to avoid border-radius looking strange with the dropdown
            // also disable default cancel cross icon in Chrome and otherWebkit browsers
            css={{
              '& input[type="search"]': { WebkitAppearance: "none" },
              '& input[type="search"]::-webkit-search-cancel-button': { display: "none" },
              "& input": { height: "2.75rem" },
            }}
            endElement={<>{ClearButton}</>}
          >
            <InputBS
              size="2xl"
              autoComplete="off"
              name="search"
              ref={inputRef}
              type="search"
              aria-label={placeholder}
              placeholder={placeholder}
              onChange={(event) => setInputValue(event.target.value)}
              value={inputValue}
              _hover={{ backgroundColor: "rgba(0,0,0,.05)" }}
              transition=".25s"
              _placeholder={{ color: paletteColor.isBright ? colors.grey90 : colors.grey10 }}
              _focusVisible={{
                backgroundColor: "white",
                paddingLeft: "1rem",
                color: colors.grey90,
                _placeholder: { color: colors.grey90 },
                _hover: { backgroundColor: "white" },
              }}
              label={t("Søk")}
              hideLabel
              variant="flushed"
              backgroundColor="transparent"
              borderBottom={`${paletteColor.isBright ? colors.grey90 : colors.grey10} .125rem solid`}
              paddingRight="3rem" // space for clear button
            />
          </InputGroup>
          <ButtonBS type="submit" variant="secondary" size="lg" paddingX={{ md: "2.5rem" }}>
            {t("Søk")}
          </ButtonBS>
        </HStack>
      </DefaultContainer>
    </Box>
  );
};
